import {
  ChargeOrTaxRule,
  ChargeTax,
  ChargeTier,
  FormCharge,
  StoreOrderingForm,
  StoreOrderingResponse,
} from '../../ordering-service.types';

const transformAmountForResponse = amount => (amount ? amount / 1000 : 0);
const transformPercentageForResponse = percentage =>
  percentage ? percentage * 100 : 0;

const transformAmountForRequest = amount => amount * 1000;
const transformPercentageForRequest = percentage => percentage / 100;

const transformChargeOrTaxRulesForResponse = (rules: ChargeOrTaxRule[]) => {
  return rules?.map(rule => {
    return { ...rule, amount: transformAmountForResponse(rule.amount) };
  });
};

const transformChargeTiersForResponse = (tiers: ChargeTier[]) =>
  tiers?.map(tier => ({
    ...tier,
    percentage: transformPercentageForResponse(tier.percentage),
    amount: transformAmountForResponse(tier.amount),
  }));

const transformChargeTaxForResponse = (taxes: ChargeTax[]) =>
  taxes?.map(tax => ({
    ...tax,
    percentage: transformPercentageForResponse(tax.percentage),
    amount: transformAmountForResponse(tax.amount),
  }));

export const transformDynamicDeliveryChargeForResponse = (
  responseData: StoreOrderingResponse,
) => {
  const dynamicDelivery = responseData?.dynamicDeliveryCharge;
  const transformed = {
    ...dynamicDelivery,
    amount: transformAmountForResponse(dynamicDelivery?.amount),
    percentage: transformPercentageForResponse(dynamicDelivery?.percentage),
    rules: transformChargeOrTaxRulesForResponse(dynamicDelivery.rules),
    enabledChargeRules: dynamicDelivery?.rules?.length > 0,
    enabledDynamicDelivery: true,
    taxes: transformChargeTaxForResponse(dynamicDelivery.taxes) ?? [],
    dynamicDelivery: transformChargeTiersForResponse(
      dynamicDelivery?.chargeTiers,
    ),
    extChargeId: '',
  };
  return transformed;
};

export const transformChargesForResponse = (
  responseData: StoreOrderingResponse,
): StoreOrderingForm['charges'] => {
  const charges = responseData?.charges;
  const transformed = charges.map(charge => ({
    ...charge,
    amount: transformAmountForResponse(charge.amount),
    percentage: transformPercentageForResponse(charge.percentage),
    rules: transformChargeOrTaxRulesForResponse(charge.rules),
    enabledChargeRules: charge?.rules?.length > 0,
    enabledDynamicDelivery: false,
    taxes: transformChargeTaxForResponse(charge.taxes) ?? [],
  }));

  // Add dynamic delivery charge to the charges array so we can work with it in the same
  // list on the form
  if (responseData?.dynamicDeliveryCharge) {
    transformed.push(transformDynamicDeliveryChargeForResponse(responseData));
  }
  return transformed;
};

export const transformTaxesForResponse = (
  responseData: StoreOrderingResponse,
): StoreOrderingForm['taxes'] => {
  const taxes = responseData?.taxes;
  const transformed = taxes.map(tax => ({
    ...tax,
    amount: transformAmountForResponse(tax.amount),
    percentage: transformPercentageForResponse(tax.percentage),
    rules: transformChargeOrTaxRulesForResponse(tax.rules),
    enabledTaxRules: tax?.rules?.length > 0,
  }));
  return transformed;
};

export const transformTaxesForRequest = (
  data: StoreOrderingForm['taxes'],
): StoreOrderingResponse['taxes'] => {
  return (
    data?.map(tax => ({
      ...tax,
      amount: transformAmountForRequest(tax.amount),
      percentage: transformPercentageForRequest(tax.percentage),
      rules: tax.rules?.map(rule => ({
        ...rule,
        amount: transformAmountForRequest(rule.amount),
      })),
    })) ?? []
  );
};

export const transformChargesForRequest = (
  data: FormCharge[],
): StoreOrderingResponse['charges'] => {
  return (
    data
      ?.filter(charge => !charge.enabledDynamicDelivery)
      ?.map(charge => ({
        ...charge,
        amount: transformAmountForRequest(charge.amount),
        percentage: transformPercentageForRequest(charge.percentage),
        rules: charge.rules?.map(rule => ({
          ...rule,
          amount: transformAmountForRequest(rule.amount),
        })),
        taxes: charge.taxes?.map(tax => ({
          ...tax,
          amount: transformAmountForRequest(tax.amount),
          percentage: transformPercentageForRequest(tax.percentage),
        })),
      })) ?? []
  );
};

export const transformDynamicDeliveryForRequest = (
  data: FormCharge[],
): StoreOrderingResponse['dynamicDeliveryCharge'] => {
  const charge = data?.find(charge => charge.enabledDynamicDelivery);

  return charge
    ? {
        ...charge,
        delivery: false,
        dynamicDelivery: false,
        gratuity: null,
        title: charge.chargeDescription,
        type: charge.chargeType,
        amount: transformAmountForRequest(charge.amount),
        percentage: transformPercentageForRequest(charge.percentage),
        rules: charge.rules?.map(rule => ({
          ...rule,
          amount: transformAmountForRequest(rule.amount),
        })),
        taxes:
          charge.taxes?.map(tax => ({
            ...tax,
            amount: transformAmountForRequest(tax.amount),
            percentage: transformPercentageForRequest(tax.percentage),
          })) ?? [],
        chargeTiers: charge.dynamicDelivery?.map(tier => ({
          ...tier,
          amount: transformAmountForRequest(tier.amount),
          percentage: transformPercentageForRequest(tier.percentage),
        })),
      }
    : null;
};
